import React, { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { LenderMappingsFormValues } from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/types';
import { SelectField } from 'components/form';
import { LenderFieldMap } from '@trnsact/trnsact-shared-types';
import {
  getSelectMappingFieldOptions,
  sanitizeFormKey,
} from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/lib';
import { FormControl, FormHelperText } from '@mui/material';

interface Props {
  field: LenderFieldMap;
  inputProps?: any;
  onValueChange: (lenderProperty: string, newValue: any) => void;
}

export const SelectMappingField = ({ field, inputProps, onValueChange }: Props) => {
  const { control } = useFormContext<LenderMappingsFormValues>();

  const options = getSelectMappingFieldOptions(field);
  const fieldName = `mappingsByLenderProperty.${sanitizeFormKey(field.lenderProperty!)}.mappedValue` as const;

  const currentValue = useWatch({ control, name: fieldName });
  const prevValueRef = useRef(currentValue);

  useEffect(() => {
    if (prevValueRef.current !== currentValue && field.lenderProperty) {
      prevValueRef.current = currentValue;
      onValueChange(field.lenderProperty, currentValue);
    }
  }, [currentValue, field.lenderProperty, onValueChange]);

  const hasError = !!field.details?.errors?.length;

  return (
    <FormControl error={hasError} fullWidth>
      <SelectField
        label={field.details?.displayName ?? ''}
        control={control}
        name={fieldName}
        options={options}
        variant="outlined"
        error={hasError}
        required={field.details?.required === 'REQUIRED'}
        inputProps={inputProps}
      />
      {hasError && <FormHelperText>{field.details?.errors?.map(e => e.message).join(', ')}</FormHelperText>}
    </FormControl>
  );
};
